import React from 'react';
import { BellIcon, CloudOffIcon, LockIcon, MessageSquareIcon, PaperclipIcon, PinIcon, RefreshCwIcon, SearchIcon, TriangleAlertIcon, UsersIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Select } from '../ui/Field';
import { useNotes } from '../../contexts/NotesContext';
import { folderPath, highlightedSnippet, matchedFields } from '../../utils/notes';
import type { Note, NotesSelection } from '../../types/notes';
export type SortKey = 'edited' | 'created' | 'title';
interface NoteListProps {
  notes: Note[];
  selection: NotesSelection;
  heading: string;
  query: string;
  onQuery: (value: string) => void;
  sort: SortKey;
  onSort: (value: SortKey) => void;
  includeArchived: boolean;
  onIncludeArchived: (value: boolean) => void;
  selectedId: string | null;
  onSelectNote: (id: string) => void;
  onNewNote: () => void;
}
export function NoteList({
  notes,
  selection,
  heading,
  query,
  onQuery,
  sort,
  onSort,
  includeArchived,
  onIncludeArchived,
  selectedId,
  onSelectNote,
  onNewNote
}: NoteListProps) {
  const {
    folders,
    emptyTrash
  } = useNotes();
  const searching = query.trim().length > 0;
  return <div className="flex h-full flex-col" data-id="element-3711" data-mp="1fip89vhw3q3b">
      <div className="border-b border-line p-3" data-id="element-3712" data-mp="1kg8ov2228eyf">
        <div className="relative" data-id="element-3713" data-mp="1kexlv4gfvb8d">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" data-id="element-3714" data-mp="nppw1k8sefyn" />
          <input type="search" value={query} onChange={event => onQuery(event.target.value)} placeholder="Search titles, text, folders, tags, attachments" aria-label="Search notes" className="h-9 w-full rounded-lg border border-line-strong bg-surface pl-9 pr-3 text-[13px] text-ink placeholder:text-ink-subtle focus:border-brand-700 focus:outline-none" data-id="element-3715" data-mp="rq9uqs90drmw" />
        </div>
        <div className="mt-2.5 flex items-center gap-2" data-id="element-3716" data-mp="2d67g943q8o69">
          <p className="cz-num flex-1 truncate text-[12px] text-ink-muted" data-id="element-3717" data-mp="1nexoclxyz5qq">
            {searching ? `${notes.length} result${notes.length === 1 ? '' : 's'} for “${query.trim()}”` : `${heading} · ${notes.length}`}
          </p>
          <Select value={sort} onChange={event => onSort(event.target.value as SortKey)} aria-label="Sort notes" className="h-8 w-auto py-0 text-[12px]" data-id="element-3718" data-mp="1xdn5gci1km5x">
            <option value="edited" data-id="element-3719" data-mp="14nek6bs7lln4">Edited</option>
            <option value="created" data-id="element-3720" data-mp="zrpj240jjwop">Created</option>
            <option value="title" data-id="element-3721" data-mp="1efkcxnqac73d">Title</option>
          </Select>
        </div>
        <label className="mt-2 flex items-center gap-2 text-[12px] text-ink-muted" data-id="element-3722" data-mp="1ehnd30l5jglt">
          <input type="checkbox" checked={includeArchived} onChange={event => onIncludeArchived(event.target.checked)} className="h-3.5 w-3.5 rounded accent-brand-700" data-id="element-3723" data-mp="19n9fqsmxizei" />
          Include archived notes in results
        </label>
        {selection.view === 'trash' && notes.length > 0 && <Button size="sm" variant="danger" className="mt-2.5 w-full" onClick={emptyTrash} data-id="element-3724" data-mp="2xdyuh0hoywjf">
            Empty trash
          </Button>}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto" data-id="element-3725" data-mp="2ir5kmaqxyiym">
        {notes.length === 0 ? <EmptyState icon={<SearchIcon className="h-4 w-4" data-id="element-3727" data-mp="2nm2v7m3vd0dm" />} title={searching ? 'Nothing matched' : 'No notes here yet'} description={searching ? `No note contains “${query.trim()}” in its title, text, folder, tags, links or attachment names${includeArchived ? '' : '. Archived notes are currently excluded'}.` : selection.view === 'trash' ? 'Deleted notes appear here for 30 days before they are permanently removed.' : selection.view === 'shared' ? 'Nobody has shared a note with you. Shared notes appear here with the role their owner granted.' : 'Create a note and it will show up in this list.'} action={!searching && selection.view !== 'trash' && selection.view !== 'shared' ? <Button variant="secondary" onClick={onNewNote} data-id="element-3728" data-mp="1wi06bmwqj8tn">
                  New note
                </Button> : undefined} data-id="element-3726" data-mp="2noytjbjmpr2l" /> : <ul className="divide-y divide-line" data-id="element-3729" data-mp="2u3o2c6pa3p4n">
            {notes.map(note => {
          const isSelected = note.id === selectedId;
          const fields = matchedFields(note, folders, query);
          return <li key={note.id} data-id="element-3730" data-mp="2g697gmg9ap5a">
                  <button type="button" onClick={() => onSelectNote(note.id)} aria-current={isSelected} className={`w-full px-4 py-3.5 text-left transition-colors duration-150 ease-out ${isSelected ? 'bg-brand-50' : 'hover:bg-canvas'}`} data-id="element-3731" data-mp="2g57kbjlyuc77">
                    <span className="flex items-start gap-2" data-id="element-3732" data-mp="3keznawvkkdx3">
                      <span className="min-w-0 flex-1" data-id="element-3733" data-mp="3kdetxakzr10t">
                        <span className="block truncate text-[13px] font-semibold text-ink" data-id="element-3734" data-mp="3pe2hfma0gzqa">
                          {note.title || 'Untitled note'}
                        </span>
                        <span className="mt-0.5 line-clamp-2 block text-[12px] leading-5 text-ink-muted" data-id="element-3735" data-mp="2y7y3mfuu6r4">
                          {note.locked ? 'Locked — unlock to read this note' : highlightedSnippet(note, query, 120)}
                        </span>
                      </span>
                      <span className="flex shrink-0 flex-col items-end gap-1" data-id="element-3736" data-mp="gy98bsi0eqj9">
                        {note.pinned && <PinIcon className="h-3.5 w-3.5 text-brand-700" data-id="element-3737" data-mp="2dt5deq3f0vme" />}
                        {note.locked && <LockIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3738" data-mp="2iqxea9m1fs2q" />}
                        {note.shares.length > 0 && <UsersIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3739" data-mp="3h285bfdlvdg6" />}
                        {note.attachments.length > 0 && <PaperclipIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3740" data-mp="3v3kjpmd4b6zy" />}
                        {(note.comments ?? []).some(comment => !comment.resolved) && <span className="cz-num inline-flex items-center gap-0.5 text-[11px] font-semibold text-brand-800" data-id="element-3741" data-mp="8t88zjczxybo">
                            <MessageSquareIcon className="h-3.5 w-3.5" data-id="element-3742" data-mp="3tvkyvae2l7i" />
                            {(note.comments ?? []).filter(comment => !comment.resolved).length}
                          </span>}
                        {note.reminder && <BellIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3743" data-mp="rmc65epfatb1" />}
                        {note.syncState === 'offline' && <CloudOffIcon className="h-3.5 w-3.5 text-warn-700" data-id="element-3744" data-mp="mk39a79fj73n" />}
                        {note.syncState === 'syncing' && <RefreshCwIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3745" data-mp="1b6169bxr9h15" />}
                        {note.syncState === 'conflict' && <TriangleAlertIcon className="h-3.5 w-3.5 text-warn-700" data-id="element-3746" data-mp="118mwfv6c7qht" />}
                      </span>
                    </span>

                    <span className="cz-num mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-ink-subtle" data-id="element-3747" data-mp="nff2vxyp4kxh">
                      <span data-id="element-3748" data-mp="djke0l3vptm9">{folderPath(note.folderId, folders)}</span>
                      <span aria-hidden="true" data-id="element-3749" data-mp="dh7n3ig588x2">·</span>
                      <span data-id="element-3750" data-mp="ihlt6uy6p8sg">
                        {sort === 'created' ? note.createdAt : note.updatedAt}
                      </span>
                      {note.ownerName !== 'Saviour Okon' && <>
                          <span aria-hidden="true" data-id="element-3751" data-mp="8jpjm7kwalwg">·</span>
                          <span data-id="element-3752" data-mp="p09tju4e2c2k">from {note.ownerName}</span>
                        </>}
                      {note.trashedOn && <>
                          <span aria-hidden="true" data-id="element-3753" data-mp="f1alacnjcig3">·</span>
                          <span data-id="element-3754" data-mp="ezprwqcyj5jt">deleted {note.trashedOn}</span>
                        </>}
                    </span>

                    {note.tags.length > 0 && <span className="mt-1.5 flex flex-wrap gap-1" data-id="element-3755" data-mp="62fg93w89eed">
                        {note.tags.map(tag => <Badge key={tag} tone="neutral" data-id="element-3756" data-mp="182vmqj0lx11">
                            #{tag}
                          </Badge>)}
                      </span>}

                    {searching && fields.length > 0 && <span className="mt-1.5 block text-[11px] text-brand-800" data-id="element-3757" data-mp="3ih80j00k0gla">
                        matched {fields.map(field => field.label.toLowerCase()).join(', ')}
                      </span>}

                    {note.lifecycle === 'archived' && <Badge tone="warn" className="mt-1.5" data-id="element-3758" data-mp="1xzgwyhh8l2ug">
                        Archived
                      </Badge>}
                  </button>
                </li>;
        })}
          </ul>}
      </div>
    </div>;
}